/**
 * Price history for one pool, for the chart on a company's page.
 *
 * A chain gives a price now, not a price yesterday: candles are built from
 * indexed swap events, which is somebody's index or ours to build. GeckoTerminal
 * publishes theirs, keyless and public, and the browser asks for it directly —
 * the same shape as the KyberSwap quote, and for the same reason: there is no
 * secret to hold, so a proxy of ours would add a hop and a service to keep up.
 *
 * Every failure returns null. A page whose chart is missing still carries the
 * price, the oracle, the depth and the swap; a page that throws carries nothing.
 */

const BASE_URL = 'https://api.geckoterminal.com/api/v2/networks/base/pools'

export interface Candle {
  /** Unix seconds of the candle's open. */
  time: number
  open: number
  high: number
  low: number
  close: number
  /** Quote-currency volume in the window. */
  volume: number
}

export type CandleRange = '1D' | '1W' | '1M' | '1Y'

interface RangeSpec {
  timeframe: 'minute' | 'hour' | 'day'
  aggregate: number
  limit: number
}

/**
 * Each range covers its own window at roughly a hundred candles, so the chart's
 * shape is the market's and not the range's.
 *
 * **The aggregate is not free to choose.** The index accepts only 1, 5 and 15
 * for minutes, 1, 4 and 12 for hours and 1 for days; anything else is a 400
 * with a message nobody sees, and the page then says "no price history for this
 * pool" about a pool with eleven million dollars of daily volume. An empty
 * chart is an unusually quiet way for a bug to fail, which is why
 * `ALLOWED_AGGREGATES` below exists and is asserted in a test.
 */
export const ALLOWED_AGGREGATES: Record<RangeSpec['timeframe'], number[]> = {
  minute: [1, 5, 15],
  hour: [1, 4, 12],
  day: [1]
}

export const RANGES: Record<CandleRange, RangeSpec> = {
  '1D': { timeframe: 'minute', aggregate: 15, limit: 96 },
  '1W': { timeframe: 'hour', aggregate: 4, limit: 42 },
  '1M': { timeframe: 'hour', aggregate: 12, limit: 60 },
  '1Y': { timeframe: 'day', aggregate: 1, limit: 365 }
}

/** GeckoTerminal returns `[time, open, high, low, close, volume]`, newest first. */
function parseCandles(payload: unknown): Candle[] {
  const list = (payload as { data?: { attributes?: { ohlcv_list?: unknown[] } } })?.data?.attributes
    ?.ohlcv_list
  if (!Array.isArray(list)) return []
  const candles: Candle[] = []
  for (const row of list) {
    if (!Array.isArray(row) || row.length < 6) continue
    const [time, open, high, low, close, volume] = row.map(Number)
    if (![time, open, high, low, close].every(Number.isFinite)) continue
    candles.push({ time, open, high, low, close, volume: Number.isFinite(volume) ? volume : 0 })
  }
  return candles.sort((a, b) => a.time - b.time)
}

/**
 * The candles, or `null` when the index could not be reached.
 *
 * The difference matters on screen: an empty array is a pool nobody has traded,
 * `null` is a question we failed to ask, and telling a visitor the first when
 * the second happened is a claim about the market that is not ours to make.
 */
export async function fetchCandles(params: {
  pool: string
  range: CandleRange
  signal?: AbortSignal
}): Promise<Candle[] | null> {
  const spec = RANGES[params.range]
  const url = `${BASE_URL}/${params.pool}/ohlcv/${spec.timeframe}?aggregate=${spec.aggregate}&limit=${spec.limit}&currency=usd`
  try {
    let response = await fetch(url, { headers: { accept: 'application/json' }, signal: params.signal })
    if (response.status === 429) {
      await new Promise((resolve) => setTimeout(resolve, 1500))
      if (params.signal?.aborted) return null
      response = await fetch(url, { headers: { accept: 'application/json' }, signal: params.signal })
    }
    if (!response.ok) return null
    return parseCandles(await response.json())
  } catch {
    return null
  }
}

export interface PoolStats {
  /** Traded in the last 24 hours, in dollars. */
  volume24hUsd: number | null
  /** The pool's own reported move over 24 hours, in percent. */
  change24hPercent: number | null
}

function num(value: unknown): number | null {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

interface PoolAttributes {
  address?: string
  volume_usd?: { h24?: string }
  price_change_percentage?: { h24?: string }
}

function statsFrom(attributes: PoolAttributes | undefined): PoolStats {
  return {
    volume24hUsd: num(attributes?.volume_usd?.h24),
    change24hPercent: num(attributes?.price_change_percentage?.h24)
  }
}

/**
 * Volume and the day's move for many pools at once.
 *
 * Thirteen separate requests on one page load is the shape that gets a public
 * API to rate-limit you; the multi endpoint takes up to thirty addresses and
 * answers in one. Chunked at thirty for the day the board is longer than that.
 */
export async function fetchManyPoolStats(params: {
  pools: string[]
  signal?: AbortSignal
}): Promise<Map<string, PoolStats>> {
  const out = new Map<string, PoolStats>()
  const unique = [...new Set(params.pools.map((p) => p.toLowerCase()))].filter(Boolean)
  const chunks: string[][] = []
  for (let i = 0; i < unique.length; i += 30) chunks.push(unique.slice(i, i + 30))
  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        /*
         * The index rate-limits, and a 429 is not a failure worth showing a
         * visitor — it is a request made a second too early. One retry, once,
         * after a pause; anything more and a page open in ten tabs becomes the
         * reason the limit exists.
         */
        let response = await fetch(`${BASE_URL}/multi/${chunk.join(',')}`, {
          headers: { accept: 'application/json' },
          signal: params.signal
        })
        if (response.status === 429) {
          await new Promise((resolve) => setTimeout(resolve, 1500))
          if (params.signal?.aborted) return
          response = await fetch(`${BASE_URL}/multi/${chunk.join(',')}`, {
            headers: { accept: 'application/json' },
            signal: params.signal
          })
        }
        if (!response.ok) return
        const payload = (await response.json()) as { data?: Array<{ attributes?: PoolAttributes }> }
        for (const entry of payload?.data ?? []) {
          const address = entry?.attributes?.address?.toLowerCase()
          if (address) out.set(address, statsFrom(entry.attributes))
        }
      } catch {
        /* One chunk missing costs those rows their volume column, nothing else. */
      }
    })
  )
  return out
}

/**
 * Volume and the 24-hour move. Both are the index's numbers, not ours, and the
 * page labels them as such — our own reads answer "now", and a day of trading
 * is exactly what a chain read cannot tell you.
 */
export async function fetchPoolStats(params: {
  pool: string
  signal?: AbortSignal
}): Promise<PoolStats | null> {
  try {
    const response = await fetch(`${BASE_URL}/${params.pool}`, {
      headers: { accept: 'application/json' },
      signal: params.signal
    })
    if (!response.ok) return null
    const payload = (await response.json()) as { data?: { attributes?: PoolAttributes } }
    return statsFrom(payload?.data?.attributes)
  } catch {
    return null
  }
}
